(() => {
  "use strict";

  const contentSelector = "[data-article-editorial]";
  const readyAttribute = "data-article-editorial-ready";
  const captionPattern = /^caption:\s*(.+)$/i;
  const directivePattern = /^(left|right|feature):\s*(.*)$/i;
  const railMedia = window.matchMedia("(min-width: 95rem)");
  let collisionFrameId = null;

  function hasOnlyWhitespaceText(element) {
    return Array.from(element.childNodes).every(
      (node) => node.nodeType !== Node.TEXT_NODE || node.textContent.trim() === "",
    );
  }

  function isSpanWrappedImage(span, image) {
    if (!hasOnlyWhitespaceText(span)) return false;
    const children = Array.from(span.children);
    return children.length === 1 && children[0] === image;
  }

  function isValidLink(link, image, allowPicture) {
    if (!hasOnlyWhitespaceText(link)) return false;

    const children = Array.from(link.children);
    if (children.length !== 1) return false;
    if (children[0] === image) return true;
    if (children[0].tagName === "SPAN" && isSpanWrappedImage(children[0], image)) return true;
    return (
      allowPicture && children[0].tagName === "PICTURE" && isValidPicture(children[0], image, false)
    );
  }

  function isValidPicture(picture, image, allowLink) {
    if (!hasOnlyWhitespaceText(picture)) return false;

    const branches = Array.from(picture.children).filter((child) => child.tagName !== "SOURCE");
    if (branches.length !== 1) return false;
    if (branches[0] === image) return true;
    return allowLink && branches[0].tagName === "A" && isValidLink(branches[0], image, false);
  }

  function parseCaptionText(text) {
    const trimmed = text.trim();
    const captionMatch = trimmed.match(captionPattern);
    if (captionMatch) return { text: captionMatch[1].trim(), directive: null };

    const directiveMatch = trimmed.match(directivePattern);
    if (directiveMatch) {
      return {
        text: directiveMatch[2].trim() || null,
        directive: directiveMatch[1].toLowerCase(),
      };
    }

    return null;
  }

  // Resolves the movable image (or its wrapper) in a block, and tolerates a
  // same-paragraph caption -- an RTE soft break (Shift+Enter) instead of a new
  // paragraph leaves "Caption: ..." as a sibling of the image inside the SAME
  // <p>, rather than in its own following <p>. Content appearing BEFORE the
  // image still always bails (preserves the "text before <img> text after"
  // fail-safe); content AFTER the image only proceeds if it parses as a
  // caption/directive -- ordinary trailing prose still bails untouched.
  function getMovableNode(block, image) {
    // DIV: RTE float/alignment image insertion sometimes wraps a single image
    // in a plain <div style="text-align: ..."> instead of a <p>. The same
    // strict single-child checks below make this exactly as narrow as the
    // existing P/H3 handling -- only a clean single-image wrapper qualifies.
    if (!block || !["P", "H3", "DIV"].includes(block.tagName)) return null;
    if (block.querySelectorAll("img").length !== 1) return null;

    let movableNode = null;
    const trailingNodes = [];

    for (const node of block.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() === "") continue;
      if (node.tagName === "BR") continue;

      if (!movableNode) {
        if (node.nodeType !== Node.ELEMENT_NODE) return null;
        movableNode = node;
        continue;
      }

      trailingNodes.push(node);
    }

    if (!movableNode) return null;

    let resolved = null;
    if (movableNode === image) resolved = image;
    else if (movableNode.tagName === "SPAN" && isSpanWrappedImage(movableNode, image))
      resolved = image;
    else if (movableNode.tagName === "A" && isValidLink(movableNode, image, true))
      resolved = movableNode;
    else if (movableNode.tagName === "PICTURE" && isValidPicture(movableNode, image, true))
      resolved = movableNode;
    if (!resolved) return null;

    if (trailingNodes.length === 0) return { node: resolved, inlineCaption: null };

    const trailingText = trailingNodes.map((node) => node.textContent).join("");
    const parsed = parseCaptionText(trailingText);
    if (!parsed) return null;

    return { node: resolved, inlineCaption: { ...parsed, nodes: trailingNodes } };
  }

  function getAlignment(image, block) {
    const candidates = [
      image.dataset.alignment,
      image.getAttribute("align"),
      image.style.float,
      block.style.textAlign,
    ];

    for (const candidate of candidates) {
      const alignment = candidate?.trim().toLowerCase();
      if (alignment === "left" || alignment === "right" || alignment === "center") return alignment;
    }

    return "center";
  }

  function normalizePositioning(image) {
    image.removeAttribute("align");
    image.style.removeProperty("float");
    image.style.removeProperty("margin-left");
    image.style.removeProperty("margin-right");
  }

  function getRatio(image) {
    const attributeWidth = Number.parseFloat(image.getAttribute("width"));
    const attributeHeight = Number.parseFloat(image.getAttribute("height"));

    if (attributeWidth > 0 && attributeHeight > 0) return attributeWidth / attributeHeight;
    if (image.naturalWidth > 0 && image.naturalHeight > 0)
      return image.naturalWidth / image.naturalHeight;
    return null;
  }

  function getSectionIndex(content, block) {
    const headings = Array.from(content.querySelectorAll(":scope > h2"));
    return headings.filter(
      (heading) => heading.compareDocumentPosition(block) & Node.DOCUMENT_POSITION_FOLLOWING,
    ).length;
  }

  // A paragraph is genuinely blank only if it has no element children besides
  // <br> -- NOT merely empty textContent, since a paragraph containing only an
  // <img> also has empty textContent and must never be treated as skippable
  // (doing so would misattribute a later caption/directive to an earlier
  // image, stealing it from the image actually adjacent to it).
  function isBlankParagraph(element) {
    return Array.from(element.childNodes).every((node) => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim() === "";
      return node.tagName === "BR";
    });
  }

  // Finds a caption/directive paragraph following `block`, skipping over any
  // genuinely blank paragraphs in between (RTE soft breaks / stray empty <p>
  // from pasted content) rather than only checking the immediate next sibling.
  //
  // Blank paragraphs are removed unconditionally here, whether or not a real
  // caption is ultimately found afterward -- a leftover empty <p> sitting
  // between two image figures would otherwise break the figure-to-figure
  // margin collapse that gives back-to-back images uniform spacing.
  function getCaption(block) {
    let candidate = block.nextElementSibling;

    while (candidate && candidate.tagName === "P" && isBlankParagraph(candidate)) {
      const blank = candidate;
      candidate = candidate.nextElementSibling;
      blank.remove();
    }

    if (!candidate || candidate.tagName !== "P") return null;

    const parsed = parseCaptionText(candidate.textContent);
    if (!parsed) return null;

    return {
      ...parsed,
      nodes: Array.from(candidate.childNodes),
      cleanupElements: [candidate],
    };
  }

  // caption.nodes are the DOM nodes to move into the figcaption -- either a
  // sibling paragraph's children (next-paragraph case) or trailing siblings
  // within the same block as the image (same-paragraph case). For the latter,
  // there is no separate element to remove: block.replaceWith(figure) below
  // discards whatever is left in `block` once movableNode has been moved out.
  function buildFigure({ block, image, movableNode, role, side, orientation, caption }) {
    const figure = document.createElement("figure");
    figure.classList.add(
      "article-figure",
      `article-figure--${role}`,
      `article-figure--${orientation}`,
    );

    if (role === "side") {
      figure.classList.add(`article-figure--side-${side}`);
    }

    normalizePositioning(image);
    image.classList.add("article-figure__image");

    let figcaption = null;
    if (caption?.text) {
      figcaption = document.createElement("figcaption");
      figcaption.className = "article-figure__caption";

      // Move the caption's own nodes into the figcaption so inline markup —
      // links especially — survives. Assigning textContent (the prior
      // approach) flattened the caption to a string and destroyed <a> tags.
      for (const node of caption.nodes) {
        figcaption.append(node);
      }

      // Strip the leading "caption:"/"left:"/"right:"/"feature:" directive from
      // the first text-bearing node, leaving nested elements untouched.
      const walker = document.createTreeWalker(figcaption, NodeFilter.SHOW_TEXT);
      let textNode = walker.nextNode();
      while (textNode) {
        if (/\S/.test(textNode.nodeValue)) {
          textNode.nodeValue = textNode.nodeValue.replace(
            /^\s*(?:caption|left|right|feature):\s*/i,
            "",
          );
          break;
        }
        textNode = walker.nextNode();
      }

      // Repair protocol-less hrefs (e.g. "marymacgill.com") that would otherwise
      // resolve as relative store paths and 404.
      for (const link of figcaption.querySelectorAll("a[href]")) {
        const href = link.getAttribute("href");
        if (href && !/^(?:https?:|mailto:|tel:|\/|#)/i.test(href)) {
          link.setAttribute("href", "https://" + href.replace(/^\/+/, ""));
        }
      }
    }

    // Next-paragraph case: remove the (now emptied, if captioned) sibling
    // caption paragraph and any blank paragraphs skipped to find it. No-op
    // for the same-paragraph case (undefined cleanupElements).
    caption?.cleanupElements?.forEach((element) => element.remove());

    block.replaceWith(figure);
    figure.append(movableNode);
    if (figcaption) figure.append(figcaption);
  }

  function findNextHeading(figure) {
    let node = figure.nextElementSibling;
    while (node && node.tagName !== "H2") node = node.nextElementSibling;
    return node;
  }

  function rectanglesOverlap(first, second, gap = 0) {
    return (
      first.left < second.right + gap &&
      first.right > second.left - gap &&
      first.top < second.bottom + gap &&
      first.bottom > second.top - gap
    );
  }

  function runCollisionChecks() {
    collisionFrameId = null;

    const contents = Array.from(document.querySelectorAll(contentSelector));
    const groups = contents.map((content) => ({
      content,
      figures: Array.from(content.querySelectorAll(".article-figure--side")),
      nonSideFigures: Array.from(
        content.querySelectorAll(".article-figure:not(.article-figure--side)"),
      ),
    }));
    const figures = groups.flatMap((group) => group.figures);

    if (!railMedia.matches) {
      figures.forEach((figure) => figure.classList.remove("article-figure--rail-collapsed"));
      return;
    }

    const staleFigures = figures.filter((figure) =>
      figure.classList.contains("article-figure--rail-collapsed"),
    );
    if (staleFigures.length > 0) {
      staleFigures.forEach((figure) => figure.classList.remove("article-figure--rail-collapsed"));
      scheduleCollisionChecks();
      return;
    }

    const measurementsByContent = groups.map(({ figures: contentFigures, nonSideFigures }) => ({
      measurements: contentFigures.map((figure) => ({
        figure,
        rect: figure.getBoundingClientRect(),
        nextHeadingRect: findNextHeading(figure)?.getBoundingClientRect() || null,
        side: figure.classList.contains("article-figure--side-left") ? "left" : "right",
      })),
      nonSideRects: nonSideFigures.map((f) => f.getBoundingClientRect()),
    }));
    const unsafeFigures = new Set();
    // 20px at the 16px base; scales with the root font size above 1512px
    const edgeGuard = 1.25 * parseFloat(getComputedStyle(document.documentElement).fontSize);

    measurementsByContent.forEach(({ measurements, nonSideRects }) => {
      const accepted = [];

      measurements.forEach((measurement) => {
        const outsideViewport =
          measurement.rect.left < edgeGuard ||
          measurement.rect.right > window.innerWidth - edgeGuard;
        const crossesHeading =
          measurement.nextHeadingRect &&
          measurement.rect.bottom > measurement.nextHeadingRect.top - 32;
        const collidesWithRail = accepted.some(
          (other) =>
            other.side === measurement.side && rectanglesOverlap(measurement.rect, other.rect, 32),
        );
        const collidesWithFigure = nonSideRects.some((rect) =>
          rectanglesOverlap(measurement.rect, rect, 32),
        );

        if (outsideViewport || crossesHeading || collidesWithRail || collidesWithFigure) {
          unsafeFigures.add(measurement.figure);
        } else {
          accepted.push(measurement);
        }
      });
    });

    figures.forEach((figure) => {
      if (unsafeFigures.has(figure)) {
        figure.classList.add("article-figure--rail-collapsed");
      } else {
        figure.classList.remove("article-figure--rail-collapsed");
      }
    });
  }

  function scheduleCollisionChecks() {
    if (collisionFrameId !== null) return;
    collisionFrameId = requestAnimationFrame(runCollisionChecks);
  }

  function enhanceContent(content) {
    if (content.hasAttribute(readyAttribute)) return;
    content.setAttribute(readyAttribute, "");

    const images = Array.from(content.querySelectorAll("img"));
    const candidates = images
      .filter((image) => !image.closest("figure"))
      .map((image) => {
        const block = image.closest("p, h3, div");
        const resolution = getMovableNode(block, image);
        return resolution
          ? {
              block,
              image,
              movableNode: resolution.node,
              inlineCaption: resolution.inlineCaption,
              loadListening: false,
              processed: false,
            }
          : null;
      })
      .filter(Boolean);
    const candidateByImage = new Map(candidates.map((candidate) => [candidate.image, candidate]));
    const usedSideSections = new Set();
    let featureAssigned = false;
    let candidateIndex = 0;

    function processCandidates() {
      while (candidateIndex < candidates.length) {
        const candidate = candidates[candidateIndex];
        const { block, image, movableNode, inlineCaption } = candidate;
        const ratio = getRatio(image);

        if (ratio === null) {
          // A candidate whose image errored can never resolve a ratio; skip it
          // so it doesn't block every following image/caption in the queue.
          if (candidate.failed) {
            candidateIndex += 1;
            continue;
          }
          return;
        }
        candidateIndex += 1;

        if (!block.isConnected) continue;

        const orientation = ratio >= 1 ? "landscape" : "portrait";
        const sectionIndex = getSectionIndex(content, block);
        const caption = inlineCaption || getCaption(block);
        const directive = caption?.directive;
        let role = "standard";
        let side = null;

        if (directive === "left" || directive === "right") {
          role = "side";
          side = directive;
          usedSideSections.add(sectionIndex);
        } else if (directive === "feature") {
          role = "feature";
          featureAssigned = true;
        } else {
          const alignment = getAlignment(image, block);
          if (
            (alignment === "left" || alignment === "right") &&
            !usedSideSections.has(sectionIndex)
          ) {
            role = "side";
            side = alignment;
            usedSideSections.add(sectionIndex);
          } else if (
            alignment === "center" &&
            orientation === "landscape" &&
            ratio >= 1.2 &&
            !featureAssigned
          ) {
            role = "feature";
            featureAssigned = true;
          }
        }

        candidate.processed = true;
        buildFigure({ block, image, movableNode, role, side, orientation, caption });
      }

      scheduleCollisionChecks();
    }

    function listenForCandidateLoad(candidate) {
      if (candidate.loadListening) return;
      candidate.loadListening = true;
      candidate.image.addEventListener(
        "load",
        () => {
          candidate.loadListening = false;
          processCandidates();
          scheduleCollisionChecks();

          if (!candidate.processed && getRatio(candidate.image) === null) {
            listenForCandidateLoad(candidate);
          }
        },
        { once: true },
      );
    }

    images.forEach((image) => {
      const candidate = candidateByImage.get(image);

      if (candidate) {
        listenForCandidateLoad(candidate);
      } else {
        image.addEventListener("load", scheduleCollisionChecks, { once: true });
      }
      image.addEventListener(
        "error",
        () => {
          const failedCandidate = candidateByImage.get(image);
          if (failedCandidate && !failedCandidate.processed) {
            failedCandidate.failed = true;
            processCandidates();
          }
          scheduleCollisionChecks();
        },
        { once: true },
      );
    });

    processCandidates();
    scheduleCollisionChecks();
  }

  function initialize(root = document) {
    if (root instanceof Element && root.matches(contentSelector)) enhanceContent(root);
    root.querySelectorAll?.(contentSelector).forEach(enhanceContent);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
  document.fonts?.ready.then(scheduleCollisionChecks);
  window.addEventListener("load", scheduleCollisionChecks, { once: true });
  window.addEventListener("resize", scheduleCollisionChecks, { passive: true });
  railMedia.addEventListener("change", scheduleCollisionChecks);
})();
